'use client';

import {Box, styled} from '@mui/material';
import {MuiNextLink} from '@/components/MuiNextLink';

type StyledChevronProps = {
  isSmallScreen?: boolean;
};

export const StyledChevron = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSmallScreen',
})<StyledChevronProps>(({ theme, isSmallScreen }) => ({
  display: 'inline-flex',
  color: theme.palette.text.dark,
  transform: 'rotate(0deg)',
  transformOrigin: '50% 50%',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.short,
  }),
  ...(!isSmallScreen && {
    'body:has([data-hero-media]) &': {
      color: theme.palette.text.primary,
    },
  }),
}));

type StyledDropdownProps = {
  isSmallScreen?: boolean;
  hasDropdown?: boolean;
};

export const StyledDropdown = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSmallScreen' && prop !== 'hasDropdown',
})<StyledDropdownProps>(({ theme, isSmallScreen, hasDropdown }) => ({
  position: isSmallScreen ? 'relative' : 'absolute',
  ...(hasDropdown && isSmallScreen && {
    display: 'none',
    '.header-navitem__dropdown:hover &': {
      display: 'block',
    },
    '.header-navitem__dropdown:focus-within &': {
      display: 'block',
    },
  }),
  top: '100%',
  left: 0,
  marginTop: isSmallScreen ? theme.spacing(0) : theme.spacing(1.5),
  padding: isSmallScreen ? theme.spacing(0) : theme.spacing(1.5),
  minWidth: '100%',
  width: isSmallScreen ? '100%' : 'auto',
  backgroundColor: theme.palette.primary.contrastText,
  boxShadow: isSmallScreen ? 'none' : '0 8px 24px rgba(0,0,0,0.12)',
  borderRadius: isSmallScreen
    ? `0 0 ${theme.spacing(1)} ${theme.spacing(1)}`
    : theme.shape.borderRadiusLg,
  zIndex: theme.zIndex.appBar + 1,
  opacity: 0,
  visibility: 'hidden',
  pointerEvents: 'none',
  transition: theme.transitions.create(['opacity', 'visibility'], {
    duration: theme.transitions.duration.short,
  }),

  // Hover bridge
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    top: `-${theme.spacing(1.5)}`,
    height: theme.spacing(1.5),
  },
}));

type StyledTopLinkProps = {
  isSmallScreen?: boolean;
};

export const StyledTopLink = styled(MuiNextLink, {
  shouldForwardProp: (prop) => prop !== 'isSmallScreen',
})<StyledTopLinkProps>(({ theme, isSmallScreen }) => ({
  position: 'relative',
  fontSize: theme.typography.navLink.fontSize,
  lineHeight: theme.typography.navLink.lineHeight,
  color: theme.palette.text.dark,
  maxWidth: '100%',
  textDecoration: 'none',
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  ...(!isSmallScreen && {
    'body:has([data-hero-media]) &': {
      color: theme.palette.text.primary,
    },
  }),
}));

type StyledSubLinkProps = {
  isSmallScreen?: boolean;
};

export const StyledSubLink = styled(MuiNextLink, {
  shouldForwardProp: (prop) => prop !== 'isSmallScreen',
})<StyledSubLinkProps>(({ theme, isSmallScreen }) => ({
  display: 'block',
  maxWidth: '100%',
  textDecoration: 'none',
  color: theme.palette.text.dark,
  fontSize: `${theme.typography.fontSize}px`,
  padding: theme.spacing(0.75, 1.5),
  borderRadius: theme.shape.borderRadiusMd,
  whiteSpace: isSmallScreen ? 'normal' : 'nowrap',
  fontWeight: theme.typography.fontWeightRegular,
  transition: theme.transitions.create(['background-color', 'font-variation-settings'], {
    duration: theme.transitions.duration.short,
  }),

  '&:hover': {
    backgroundColor: theme.palette.background.lightGrey,
  },
}));

type StyledNavItemProps = {
  hasDropdown: boolean;
};

export const StyledNavItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasDropdown',
})<StyledNavItemProps>(({ hasDropdown }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  cursor: 'pointer',

  '&:hover .header-navlink__chevron': {
    transform: 'rotate(180deg)',
  },
  '&:focus-within .header-navlink__chevron': {
    transform: 'rotate(180deg)',
  },

  ...(hasDropdown && {
    '&:hover .header-navlink__dropdown': {
      opacity: 1,
      visibility: 'visible',
      pointerEvents: 'auto',
    },
    '&:focus-within .header-navlink__dropdown': {
      opacity: 1,
      visibility: 'visible',
      pointerEvents: 'auto',
    },
  }),
}));


type StyledNavItemContentProps = {
  isActive: boolean;
};

export const StyledNavItemContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<StyledNavItemContentProps>(({ theme, isActive }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: -4,
    height: 4,
    width: '100%',
    backgroundColor: theme.palette.text.linkSecondary,
    transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'none',
  },
  '&:hover::after': {
    transform: 'scaleX(1)',
    transition: 'transform 200ms ease',
  },
  '&:focus-visible::after': {
    transform: 'scaleX(1)',
    transition: 'transform 200ms ease',
  },
}));